import { motion } from "framer-motion";
import useInView from "../hooks/useInView";

const CHAPTERS = [
  {
    number: "I",
    label: "Origin",
    body:
      "Jy Tristian Wright is a director, writer, and producer — but the title he wears first is father. Born and raised in Rowland Heights, California, he grew up dreaming of inspiring people the way Michael Jackson inspired people — but through movies. His journey in entertainment began at five and never let up.",
  },
  {
    number: "II",
    label: "The Craft",
    body:
      "By fourteen he was training professionally as a dancer. He majored in dance in college, earned his dance-teaching certification, and signed with his first Hollywood agency — collaborating with The Black Eyed Peas, Judith Hill, Cartoon Network, YouTube, Bobby Valentino, the Fresh Empire campaign, and the major comedy clubs of Los Angeles: The Comedy Store, The Ice House, and Flappers.",
  },
  {
    number: "III",
    label: "The Mission",
    body:
      "These last few years, Jy has been moving his light from in front of the lens to behind it — directing original films and quietly mentoring the next generation on every set he runs. The mission is plain: to show light in the entertainment industry through the beautiful eyes of a camera, and to make sure every kid knows they can dream — and that everything is going to be okay.",
    highlight: true,
  },
];

const STATS = [
  { v: "29", l: "Years on stages, sets & stories" },
  { v: "09", l: "Hollywood collabs & credits" },
  { v: "01", l: "Mission — show light in the industry" },
];

const ChapterBlock = ({ chapter, index }) => {
  const [ref, inView] = useInView({ threshold: 0.12 });
  return (
    <motion.article
      ref={ref}
      data-testid={`about-bio-${index + 1}`}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1.15, ease: [0.16, 1, 0.3, 1] }}
      className="grid grid-cols-12 gap-4 md:gap-8 items-baseline"
    >
      <div className="col-span-2 md:col-span-1">
        <div className="font-display text-5xl md:text-6xl text-sun leading-none">
          {chapter.number}
        </div>
      </div>
      <div className="col-span-10 md:col-span-11">
        <div className="font-mono text-[10px] tracking-widest2 uppercase text-muted mb-3">
          Chapter {chapter.number} — {chapter.label}
        </div>
        <p
          className={`font-display text-2xl md:text-3xl lg:text-[2.35rem] leading-[1.18] ${
            chapter.highlight ? "text-ink" : "text-ink/85"
          }`}
        >
          {chapter.body}
        </p>
      </div>
    </motion.article>
  );
};

export const Manifesto = () => {
  const [headRef, headIn] = useInView({ threshold: 0.15 });
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative bg-bg py-32 md:py-48 border-t border-white/[0.05]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-16 md:mb-24 font-mono text-[11px] tracking-widest2 uppercase text-muted">
          <span className="w-10 h-px bg-sun" />
          <span className="text-sun">(02)</span>
          <span>Manifesto — in three chapters</span>
        </div>

        <motion.h2
          ref={headRef}
          data-testid="about-heading"
          initial={{ opacity: 0, y: 40 }}
          animate={headIn ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-ink text-[13vw] md:text-[8.5vw] lg:text-[7vw] leading-[0.9] tracking-tight max-w-[16ch] mb-24"
        >
          I write for the kid <em className="italic text-sun">in the back of the theater.</em>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-9 lg:col-start-4 space-y-20 md:space-y-28">
            {CHAPTERS.map((c, i) => (
              <ChapterBlock key={c.number} chapter={c} index={i} />
            ))}
          </div>
        </div>

        {/* Stats strip */}
        <div
          data-testid="about-stats"
          className="mt-32 md:mt-40 border-t border-white/10 pt-12 grid grid-cols-1 md:grid-cols-3 gap-12"
        >
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              data-testid={`about-stat-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
            >
              <div className="font-display text-6xl md:text-7xl text-sun leading-none">{s.v}</div>
              <div className="mt-4 font-mono text-[10px] tracking-widest2 uppercase text-muted">
                {s.l}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
